import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import {
  ExportDataCatalogDatasetVariableName,
  SendToDocumentVariableName,
} from 'analysis-tool/AnalysisStepConfigVariableNames';
import {SHARED_MAIN_VIEW_COMPONENT_WITH_MARGIN_STYLES} from 'analysis-tool/AnalysisToolConstants';
import {AnalysisGraphQL, isAnalysisOutputStepType} from 'analysis-tool/AnalysisTypes';
import {
  areStepDataFrameLineageNodesEqual,
  StepDataFrameLineageNode,
} from 'analysis-tool/data/DatasetLineageUtils';
import getDownstreamSteps from 'analysis-tool/data/getDownstreamSteps';
import EntryAndWorksheetSearchInput from 'analysis-tool/outputs/EntryAndWorksheetSearchInput';
import OutputsList from 'analysis-tool/outputs/OutputsList';
import {getOutputNameFromStep, isSendToEntryEnabled} from 'analysis-tool/outputs/OutputsUtil';
import getDefaultMainViewItemForAnalysis from 'analysis-tool/ui/getDefaultMainViewItemForAnalysis';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import FolderSelector from 'file-browser/FolderSelector';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  APPLY_ANALYSIS_TEMPLATE_STARTED_EVENT,
  APPLY_ANALYSIS_TEMPLATE_FINISHED_EVENT,
  RUN_ANALYSIS_TEMPLATE_STARTED_EVENT,
} from 'performance/processAnalysesEvents';
import {
  FONT_PROXIMA_13,
  FONT_WEIGHT_PROXIMA_REGULAR,
  FONT_WEIGHT_PROXIMA_SEMIBOLD,
} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import runModal from 'ui-library/runModal';
import {withLoadingScreen} from 'util/Promises';
import {InputChangeEvent} from 'util/Types';

import * as AnalysisTemplateActions from './AnalysisTemplateActions';
import {TemplateVariableBinding} from './AnalysisTemplateDAO';
import AnalysisTemplateSelector from './AnalysisTemplateSelector';
import AnalysisTemplateTableSelectorModal, {
  AnalysisTemplateTableSelectorModalProps,
} from './AnalysisTemplateTableSelectorModal';
import {InputTableApplyState, InputTableType, InputTableLineageNode} from './AnalysisTemplateUtil';
import ApplyAnalysisTemplatePreview from './ApplyAnalysisTemplatePreview';
import useAnalysisTemplateSelectorState, {TemplateApplyState} from './useAnalysisTemplateSelectorState';

export function InputTableCard({
  analysis,
  index,
  inputTable,
  onChange,
  getInputTableDisabledMessage,
}: {
  analysis: AnalysisGraphQL;
  index: number;
  inputTable: InputTableApplyState;
  onChange: (result: {
    lineageNode: InputTableLineageNode;
    columnMapping: {[templateColumn: string]: string};
  }) => void;
  getInputTableDisabledMessage?: (
    analysis: AnalysisGraphQL,
    inputLineageNode: StepDataFrameLineageNode,
    selectedLineageNode: StepDataFrameLineageNode | null
  ) => string | null;
}): JSX.Element {
  const isStatic = inputTable.inputType === InputTableType.STATIC;
  const description = isStatic
    ? 'This table provides static data and will be added to your analysis'
    : inputTable.description;

  const onOpenModal = (): void => {
    runModal<AnalysisTemplateTableSelectorModalProps>(AnalysisTemplateTableSelectorModal, {
      analysis,
      inputTable,
      onRequestSubmit: onChange,
      getInputTableDisabledMessage,
    });
  };

  return (
    <div className={classNames(css(cardStyles.wrapper), isStatic && css(cardStyles.staticWrapper))}>
      <FieldLabel
        className={classNames(css(cardStyles.header), isStatic && css(cardStyles.staticHeader))}
        label={
          <>
            <BenchlingIcon icon="table" />
            {inputTable.templateLineageNode.data.dataFrame.name}
            {isStatic && <span className={css(cardStyles.staticLabel)}>Static</span>}
          </>
        }
      />
      <div className={css(cardStyles.description)}>{description}</div>
      <div>
        {isStatic ? null : inputTable.lineageNode == null ? (
          <Button
            buttonStyle={ButtonStyle.PRIMARY}
            size={ButtonSize.SMALL}
            fullWidth={true}
            onClick={onOpenModal}
            text="Select a table to use"
            dataTestAttrs={{
              component: 'InputTableCard',
              element: 'selectTableButton',
              index: String(index),
              key: inputTable.templateLineageNode.data.dataFrame.name,
            }}
          />
        ) : (
          <div className={css(cardStyles.body)}>
            {inputTable.lineageNode.data.dataFrame.name}
            <a onClick={onOpenModal}>
              <BenchlingIcon icon="edit" className={css(cardStyles.editIcon)} />
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

const cardStyles = StyleSheet.create({
  wrapper: {
    border: `1px solid ${Colors.GRAY_LIGHT}`,
    borderRadius: 4,
    padding: 8,
    marginBottom: 4,
    ':hover': {
      borderColor: Colors.GRAY,
      boxShadow: `0px 2px 6px 1px ${Colors.SILVER}`,
    },
  },
  staticWrapper: {
    background: Colors.GRAY_LIGHTER,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '6px',
    wordBreak: 'break-word',
  },
  staticHeader: {color: Colors.GRAY_DARKER},
  staticLabel: {
    fontStyle: 'italic',
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
  },
  description: {color: Colors.GRAY_SEMIDARK, marginBottom: 8},
  body: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  editIcon: {color: Colors.GRAY_DARK},
});

function getDocumentSelectorMessage(): string {
  if (isSendToEntryEnabled() && BenchlingConfig.ENABLE_ANALYSIS_TOOL_SEND_TO_WORKSHEET) {
    return 'Select an entry or worksheet to insert outputs into';
  } else if (isSendToEntryEnabled()) {
    return 'Select an entry to insert outputs into';
  }
  return 'Select a worksheet to insert outputs into';
}

export default function AnalysisTemplateApplyView({
  analysis,
  onCloseStepInitializationView,
}: MainViewComponentProps<AnalysisMainViewItemType.TEMPLATE_APPLY>): JSX.Element {
  const [templateApplyState, setTemplateApplyState, setSelectedTemplate] = useAnalysisTemplateSelectorState(
    analysis.id
  );
  const [isGenerateOutputsChecked, setGenerateOutputsChecked] = useState<boolean>(
    BenchlingConfig.ENABLE_ANALYSIS_TOOL_TEMPLATES_OUTPUTS
  );
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);

  const outputSteps = templateApplyState.selectedTemplate
    ? templateApplyState.selectedTemplate.execution.steps.filter((step) =>
        isAnalysisOutputStepType(step.definition.stepType)
      )
    : [];
  const exportDatasetSteps = outputSteps.filter(
    (step) => step.definition.stepType === PipelineStepType.EXPORT_DATA_CATALOG_DATASET
  );
  const sendToDocumentSteps = outputSteps.filter(
    (step) => step.definition.stepType === PipelineStepType.SEND_TO_DOCUMENT
  );

  const showFolderSelector = exportDatasetSteps.length > 0 && isGenerateOutputsChecked;
  const showDocumentInput = sendToDocumentSteps.length > 0 && isGenerateOutputsChecked;
  const shouldGenerateOutputs = outputSteps.length > 0 && isGenerateOutputsChecked;

  function closeTemplateApplyView(): void {
    onCloseStepInitializationView(getDefaultMainViewItemForAnalysis(analysis));
    TemplateApplyState.deleteState(analysis.id);
  }

  async function handleSubmit(): Promise<void> {
    logProfilerTimingEvent(APPLY_ANALYSIS_TEMPLATE_STARTED_EVENT);
    assert(templateApplyState.selectedTemplate != null, 'Expected selectedTemplate to be set');
    assert(
      templateApplyState.inputTables
        .filter((inputTable) => inputTable.inputType === InputTableType.VARIABLE)
        .every((inputTable) => inputTable.lineageNode != null),
      'Expected a table to be set for all variables'
    );

    const templateVariableBindings: Array<TemplateVariableBinding> = [];
    if (shouldGenerateOutputs) {
      assert(
        exportDatasetSteps.length === 0 || selectedFolderId != null,
        'Expected folder to be selected if export dataset steps exist'
      );
      assert(selectedDocumentId != null, 'Expected document to be selected if outputs enabled');

      templateVariableBindings.push(
        ...templateApplyState.selectedTemplate.configVariables.map((configVariable) => {
          let variableValue;
          if (configVariable.configVariableKey === ExportDataCatalogDatasetVariableName.FOLDER_ID) {
            variableValue = selectedFolderId;
          } else if (configVariable.configVariableKey === ExportDataCatalogDatasetVariableName.DATASET_NAME) {
            variableValue = getOutputNameFromStep(
              templateApplyState.selectedTemplate!,
              analysis.name,
              templateApplyState.selectedTemplate!.execution.steps.find(
                (step) => step.id === configVariable.pipelineStep.id
              )!,
              templateApplyState.inputTables
            );
          } else {
            assert(
              configVariable.configVariableKey === SendToDocumentVariableName.DOCUMENT_ID,
              'Unexpected template variable config key',
              {configKey: configVariable.configVariableKey}
            );
            variableValue = selectedDocumentId;
          }

          return {
            variableId: configVariable.id,
            variableValue,
          };
        })
      );
    }

    await withLoadingScreen(
      AnalysisTemplateActions.applyAnalysisTemplate(analysis.id, {
        templateId: templateApplyState.selectedTemplate.id,
        templateVariableBindings,
        dataFrameVariableBindings: templateApplyState.inputTables
          .filter((table) => table.inputType === InputTableType.VARIABLE)
          .map((inputTable) => ({
            variableId: inputTable.variableId!,
            newDataFrameId: inputTable.lineageNode!.data.dataFrame.id,
            newInputStepDataId: inputTable.lineageNode!.data.outputStepData.id,
            columnMapping: inputTable.columnMapping,
          })),
        shouldGenerateOutputs,
      })
    );
    closeTemplateApplyView();
    logProfilerTimingEvent(APPLY_ANALYSIS_TEMPLATE_FINISHED_EVENT);
    logProfilerTimingEvent(RUN_ANALYSIS_TEMPLATE_STARTED_EVENT);
  }

  function getInputTableDisabledMessage(
    analysis: AnalysisGraphQL,
    InputLineageNode: StepDataFrameLineageNode,
    selectedLineageNode: StepDataFrameLineageNode | null
  ): string | null {
    const downstreamSteps = [
      ...getDownstreamSteps(analysis, InputLineageNode.step.id, InputLineageNode.data?.dataFrame.id),
    ];
    if (downstreamSteps.some((step) => isAnalysisOutputStepType(step.definition.stepType))) {
      return 'Table cannot be selected because it is used to create outputs';
    }
    const isInputTableAlreadyUsed = templateApplyState.inputTables.some(
      (inputTable) =>
        inputTable.lineageNode != null &&
        areStepDataFrameLineageNodesEqual(inputTable.lineageNode, InputLineageNode)
    );
    const isInputTableSelected =
      selectedLineageNode != null && areStepDataFrameLineageNodesEqual(selectedLineageNode, InputLineageNode);
    if (isInputTableAlreadyUsed && !isInputTableSelected) {
      return 'Table is already used as a variable starting table';
    }
    return null;
  }

  return (
    <div
      className={css(viewStyles.wrapper)}
      {...getTestProps({component: 'AnalysisTemplateApplyView', element: 'root'})}
    >
      <div className={css(viewStyles.previewAreaPane)}>
        <ApplyAnalysisTemplatePreview templateAnalysis={templateApplyState.selectedTemplate} />
      </div>
      <div className={css(viewStyles.rightSidebar)}>
        <div>
          <FieldLabel label="Apply analysis template" />
          <AnalysisTemplateSelector
            value={templateApplyState.selectedTemplate}
            onSelect={setSelectedTemplate}
          />
        </div>
        {templateApplyState.inputTables.map((inputTable, index) => (
          <InputTableCard
            index={index}
            key={index}
            analysis={analysis}
            inputTable={inputTable}
            onChange={({
              lineageNode,
              columnMapping,
            }: {
              lineageNode: InputTableLineageNode;
              columnMapping: {[templateColumn: string]: string};
            }) => {
              setTemplateApplyState({
                ...templateApplyState,
                inputTables: templateApplyState.inputTables.map((editingInputTable, i) =>
                  i === index ? {...editingInputTable, lineageNode, columnMapping} : editingInputTable
                ),
              });
            }}
            getInputTableDisabledMessage={getInputTableDisabledMessage}
          />
        ))}
        <div className={css(viewStyles.divider)} />
        {BenchlingConfig.ENABLE_ANALYSIS_TOOL_TEMPLATES_OUTPUTS && outputSteps.length > 0 && (
          <div className={css(viewStyles.checkboxInput)}>
            <CheckboxInput
              id="generate-outputs"
              checked={isGenerateOutputsChecked}
              label="Generate outputs"
              onChange={(e: InputChangeEvent) => {
                setGenerateOutputsChecked(e.target.checked);
              }}
            />
          </div>
        )}
        {shouldGenerateOutputs && templateApplyState.selectedTemplate && (
          <OutputsList
            analysis={analysis}
            template={templateApplyState.selectedTemplate}
            outputSteps={outputSteps}
            inputTables={templateApplyState.inputTables}
          />
        )}
        {showFolderSelector && (
          <>
            <span className={css(viewStyles.outputVariableText)}>Select a folder for saved datasets</span>
            <FolderSelector
              value={selectedFolderId}
              onChange={(e: {target: {value: string}}) => {
                setSelectedFolderId(e.target.value);
              }}
              // This prop allows us to select non-root folders
              showInventory={true}
            />
          </>
        )}
        {showDocumentInput && (
          <>
            <span className={css(viewStyles.outputVariableText)}>{getDocumentSelectorMessage()}</span>
            <EntryAndWorksheetSearchInput
              autoFocus={false}
              isMulti={false}
              enableSearchEntries={isSendToEntryEnabled()}
              enableSearchWorksheets={BenchlingConfig.ENABLE_ANALYSIS_TOOL_SEND_TO_WORKSHEET}
              onRemove={() => {
                setSelectedDocumentId(null);
              }}
              onSelect={setSelectedDocumentId}
              dropLeft={true}
              selected={selectedDocumentId != null ? [selectedDocumentId] : []}
            />
          </>
        )}
        <div className={css(viewStyles.footerButtons)}>
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            onClick={closeTemplateApplyView}
            size={ButtonSize.MEDIUM}
            text="Cancel"
          />
          <Button
            buttonStyle={ButtonStyle.SECONDARY}
            disabled={
              templateApplyState.selectedTemplate == null ||
              templateApplyState.inputTables.filter(
                (inputTable) =>
                  inputTable.inputType === InputTableType.VARIABLE && inputTable.lineageNode == null
              ).length !== 0 ||
              (showFolderSelector && selectedFolderId == null) ||
              (showDocumentInput && selectedDocumentId == null)
            }
            onClick={handleSubmit}
            size={ButtonSize.MEDIUM}
            text="Apply"
            dataTestAttrs={{component: 'AnalysisTemplateApplyView', element: 'applyTemplateButton'}}
          />
        </div>
      </div>
    </div>
  );
}

const viewStyles = StyleSheet.create({
  previewAreaPane: {
    ...SHARED_MAIN_VIEW_COMPONENT_WITH_MARGIN_STYLES,
    overflowY: 'auto',
    height: '100%',
    margin: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    background: Colors.GRAY_LIGHTER,
    flexDirection: 'column',
    flexGrow: 1,
    overflowX: 'hidden',
  },
  wrapper: {
    backgroundColor: Colors.WHITE,
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    width: '100%',
  },
  rightSidebar: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: 8,
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 8,
    width: 223,
  },
  divider: {borderTop: `1px solid ${Colors.GRAY_LIGHT}`},
  footerButtons: {display: 'flex', justifyContent: 'flex-end', columnGap: '8px'},
  checkboxInput: {...FONT_PROXIMA_13},
  outputVariableText: {...FONT_PROXIMA_13, fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD},
});
